import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart3, CheckCircle2, Clock, AlertTriangle, Search } from 'lucide-react';
import PipelineTabs from '@/components/PipelineTabs';

const STATUS_COLORS = {
  completed: '#10b981', in_progress: '#3b82f6', review: '#8b5cf6',
  not_started: '#71717a', blocked: '#ef4444',
};

export default function VendorTrackerPage() {
  const { api } = useAuth();
  const [shots, setShots] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deptTab, setDeptTab] = useState('all');

  useEffect(() => {
    api.get('/vendor/shot-tracker').then(r => setShots(r.data)).catch(() => {});
  }, [api]);

  const filtered = shots.filter(s => {
    if (deptTab !== 'all' && s.task_type !== deptTab) return false;
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    if (search && !s.name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const completed = shots.filter(s => s.status === 'completed').length;
  const total = shots.length;
  const progress = total > 0 ? Math.round(completed / total * 100) : 0;

  return (
    <div className="space-y-4" data-testid="vendor-tracker-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Shot Tracker</h1>
          <p className="text-xs text-zinc-500 mt-1">{completed}/{total} shots completed ({progress}%) — auto-updated from artist submissions</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="w-full h-4 bg-zinc-800 rounded-sm overflow-hidden flex">
              {['completed', 'review', 'in_progress', 'not_started', 'blocked'].map(status => {
                const count = shots.filter(s => s.status === status).length;
                const pct = total > 0 ? (count / total * 100) : 0;
                return pct > 0 ? (
                  <div key={status} style={{ width: `${pct}%`, backgroundColor: STATUS_COLORS[status] }} title={`${status}: ${count}`} />
                ) : null;
              })}
            </div>
          </div>
          <span className="text-lg font-heading font-bold text-white">{progress}%</span>
        </div>
        <div className="flex gap-4 mt-2">
          {Object.entries(STATUS_COLORS).map(([s, c]) => {
            const count = shots.filter(sh => sh.status === s).length;
            return count > 0 ? (
              <span key={s} className="flex items-center gap-1 text-[9px] text-zinc-500">
                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: c }} />
                {s.replace('_', ' ')}: {count}
              </span>
            ) : null;
          })}
        </div>
      </div>

      {/* Pipeline Tabs */}
      <PipelineTabs tasks={shots} activeTab={deptTab} onTabChange={setDeptTab} showAll={true} />

      {/* Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search shots..."
            className="w-full h-8 pl-9 pr-3 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="h-8 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-3">
          <option value="all">All Status</option>
          {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
      </div>

      {/* Shot Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800 text-[10px] text-zinc-500 uppercase tracking-wider">
              <th className="text-left px-4 py-2.5 font-medium">Shot</th>
              <th className="text-left px-4 py-2.5 font-medium">Department</th>
              <th className="text-left px-4 py-2.5 font-medium">Status</th>
              <th className="text-left px-4 py-2.5 font-medium">Progress</th>
              <th className="text-left px-4 py-2.5 font-medium">Due Date</th>
              <th className="text-left px-4 py-2.5 font-medium">Priority</th>
              <th className="text-left px-4 py-2.5 font-medium">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/30">
            {filtered.map(s => {
              const isOverdue = s.due_date && new Date(s.due_date) < new Date() && s.status !== 'completed';
              return (
                <tr key={s.id} className={`hover:bg-zinc-800/20 ${isOverdue ? 'bg-red-500/5' : ''}`}>
                  <td className="px-4 py-2 text-xs text-zinc-200 font-medium">{s.name}</td>
                  <td className="px-4 py-2">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded capitalize" style={{ backgroundColor: `${STATUS_COLORS[s.task_type] || '#3b82f6'}20`, color: STATUS_COLORS[s.task_type] || '#60a5fa' }}>
                      {s.task_type}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[s.status] || '#71717a' }} />
                      <span className="text-[10px] text-zinc-400 capitalize">{s.status?.replace('_', ' ')}</span>
                      {isOverdue && <AlertTriangle className="w-3 h-3 text-red-400" />}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${s.progress}%`, backgroundColor: STATUS_COLORS[s.status] || '#3b82f6' }} />
                      </div>
                      <span className="text-[9px] font-mono text-zinc-500">{s.progress}%</span>
                    </div>
                  </td>
                  <td className={`px-4 py-2 text-[10px] font-mono ${isOverdue ? 'text-red-400' : 'text-zinc-500'}`}>
                    {s.due_date ? new Date(s.due_date).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-2 text-[10px] font-mono text-zinc-500">P{s.priority || 3}</td>
                  <td className="px-4 py-2 text-[10px] font-mono text-emerald-400">{s.rate_amount?.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-xs text-zinc-600 text-center py-6">No shots found</p>}
      </div>
    </div>
  );
}
